import { saveInventoryDevice } from 'modules/inventory/sensorInventory';
import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Tag } from 'antd';
import { useSelector } from 'react-redux';
import { AppState } from '../../../store';
import { useTranslation } from 'react-i18next';
import {
  InventoryStatus,
  InventoryStatusMap,
  InventoryContextForType,
  InventoryType,
} from '../../../modules/inventory/types';
import Message from '../../../components/Notification/Message';
import TextArea from 'antd/es/input/TextArea';
import { GPSItem } from 'modules/inventory/gps/types';

const FormItem = Form.Item;
const Option = Select.Option;
const formItemLayout = {
  wrapperCol: { span: 24 },
};
interface Props {
  id: string;
  onClose: () => void;
  inventoryTagsMap: { [id: string]: { id: string; title: string } };
  simcards: InventoryContextForType<InventoryType.SIM_CARDS>[];
}

export default function EditForm({ id, onClose, inventoryTagsMap, simcards }: Props) {
  const [form] = Form.useForm();
  const ipad = useSelector((state: AppState) => state.inventory.inventoryMap[id]) as GPSItem;
  const orgs = useSelector((state: AppState) =>
    state.orgs.orgs
      .map(({ id, title, groupId }: { id: string; title: string; groupId?: string | null }) => ({
        id,
        title,
        groupId,
      }))
      .sort((a: any, b: any) => (a?.title?.toLowerCase() > b?.title?.toLowerCase() ? 1 : -1))
  );
  const groupMap = useSelector((state: AppState) => state.groups.groupsMap);

  const initialValues = ipad;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    form.validateFields();
    return () => {};
  }, []);

  const updateInventoryItem = async (
    values: Partial<InventoryContextForType<InventoryType.GPS>>
  ) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await saveInventoryDevice({
        ...values,
        id,
        type: InventoryType.GPS,
        sim: values.sim || null,
      });
      onClose();

      Message({ key: 'editInventory', message: 'Lager og SIM er gemt' });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Kunne ikke gemme. Prøv igen.');
    } finally {
      setSaving(false);
    }
  };

  const options = simcards.map(({ id: simId, iccid, internalId, phoneNumber, connection }) => ({
    label: [iccid, internalId, phoneNumber, connection?.title].filter(Boolean).join(' · '),
    value: simId,
    disabled: Boolean(connection?.deviceId && connection.deviceId !== id),
  }));

  return (
    <>
      <Form
        form={form}
        onFinish={(values) => updateInventoryItem(values)}
        initialValues={initialValues}
      >
        <FormItem
          {...formItemLayout}
          name="identifier"
          rules={[
            {
              required: true,
              message: 'Please input serial!',
            },
          ]}
        >
          <Input type="text" placeholder={t('inventory.ipad.serial')} disabled />
        </FormItem>
        <FormItem
          {...formItemLayout}
          name="name"
          rules={[{ required: true, message: 'Please input name!' }]}
        >
          <Input type="text" placeholder={t('inventory.ipad.name')} />
        </FormItem>

        <FormItem name="internalId" label="Internt ID">
          <Input placeholder="Fx 87003" maxLength={100} />
        </FormItem>
        <FormItem
          {...formItemLayout}
          name="sim"
          rules={[{ required: false, message: 'Please input name!' }]}
        >
          <Select
            showSearch
            allowClear
            placeholder={t('inventory.ipad.sim')}
            optionFilterProp="children"
            filterOption={(input, option) =>
              // @ts-ignore
              (option?.label ?? '').includes(input)
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? '')
                // @ts-ignore
                .toLowerCase()
                // @ts-ignore
                .localeCompare((optionB?.label ?? '').toLowerCase())
            }
            options={options}
          />
          {/* {options.map(({ label, value }) => (
                            <Option value={value}>{label}</Option>
                        ))}
                    </Select> */}
        </FormItem>

        <FormItem
          {...formItemLayout}
          style={{ minWidth: '150px' }}
          name="status"
          rules={[{ required: true, message: 'Please select status!' }]}
        >
          <Select placeholder={t('access.add.role_placeholder')}>
            <Option value={InventoryStatus.NEEDS_ORDER}>
              <Tag color="red">{InventoryStatusMap[InventoryStatus.NEEDS_ORDER]}</Tag>
            </Option>
            <Option value={InventoryStatus.ORDERED}>
              <Tag color="gold">{InventoryStatusMap[InventoryStatus.ORDERED]}</Tag>
            </Option>
            <Option value={InventoryStatus.IN_STORAGE}>
              <Tag color="orange">{InventoryStatusMap[InventoryStatus.IN_STORAGE]}</Tag>
            </Option>
            <Option value={InventoryStatus.READY}>
              <Tag color="blue">{InventoryStatusMap[InventoryStatus.READY]}</Tag>
            </Option>
            <Option value={InventoryStatus.ASSIGNED}>
              <Tag color="green">{InventoryStatusMap[InventoryStatus.ASSIGNED]}</Tag>
            </Option>
          </Select>
        </FormItem>
        <FormItem
          {...formItemLayout}
          style={{ minWidth: '150px' }}
          name="tags"
          rules={[{ required: false, message: 'Please select status!' }]}
        >
          <Select placeholder={t('Tags')} mode="multiple">
            {Object.entries(inventoryTagsMap).map(([key, value]) => (
              <Option value={key}>{value.title}</Option>
            ))}
          </Select>
        </FormItem>
        <FormItem
          {...formItemLayout}
          style={{ minWidth: '150px' }}
          name="orgId"
          rules={[{ required: true, message: 'Please select company!' }]}
        >
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Vælg firma"
            optionFilterProp="children"
            filterOption={(input, option) =>
              // @ts-ignore
              option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {orgs.map((org: { id: string; title: string; groupId?: string | null }) => (
              <Option value={org.id}>
                {`${org.title} ${org?.groupId ? `(${groupMap[org.groupId]?.title})` : ''}`}
              </Option>
            ))}
          </Select>
        </FormItem>
        <FormItem {...formItemLayout} name="note" rules={[{ required: false }]}>
          <TextArea placeholder={t('Note')} />
        </FormItem>
        <FormItem>
          <Button type="primary" block htmlType="submit" loading={saving}>
            {'Gem'}
          </Button>
        </FormItem>
      </Form>
      {error && (
        <div role="alert" style={{ color: 'red' }}>
          {error}
        </div>
      )}
    </>
  );
}
